import { FRESH_FIND_STATUS, normalizeFreshFindStatus } from "../constants/availability";
import { getSafeDistanceMiles, normalizeLocationRecord } from "./locationUtils";
import { isPickupSpot, isPublicFarmStand } from "./farmStandPublic";
import { canShowStandDirections } from "./standLocation";

const hasRecordField = (record = {}, field) =>
  Object.prototype.hasOwnProperty.call(record || {}, field);

export const getHomeStandName = (stand = {}) =>
  stand.standName || stand.businessName || stand.farmName || stand.name || "Local stand / pickup spot";

export const getHomeStandStatus = (stand = {}) => {
  const freshFindsStatus = normalizeFreshFindStatus(stand?.freshFindsStatus);
  if (freshFindsStatus) return freshFindsStatus;
  const serviceStatus = normalizeFreshFindStatus(stand?.serviceAvailabilityStatus);
  if (serviceStatus) return serviceStatus;
  if (stand?.standSelfServe24_7 === true) return FRESH_FIND_STATUS.SELF_SERVE;
  return normalizeFreshFindStatus(stand?.standAvailabilityStatus) || FRESH_FIND_STATUS.CLOSED;
};

export const getHomeStandLocationLabel = (stand = {}) => {
  const primary =
    stand.publicLocationLabel ||
    stand.locationText ||
    [stand.city, stand.state].filter(Boolean).join(", ") ||
    [stand.county, stand.state].filter(Boolean).join(", ") ||
    stand.location ||
    "";
  const locality = [stand.city, stand.state].filter(Boolean).join(", ");

  if (primary && locality && primary !== locality) return `${primary} · ${locality}`;
  return primary || locality || "Location not listed";
};

export const standCanShowHomeDirections = (stand = {}) => canShowStandDirections(stand);

export const isHomeFarmStandDiscoverable = (stand = {}) =>
  isPublicFarmStand(stand) &&
  !isPickupSpot(stand) &&
  stand.isPublic !== false &&
  stand.visibility !== "private";

export const getHomeFarmStandExclusionReasons = (stand = {}) => {
  const reasons = [];
  if (!isPublicFarmStand(stand)) reasons.push("not public active stand");
  if (isPickupSpot(stand)) reasons.push("classified as pickup spot");
  if (stand.isPublic === false || stand.visibility === "private") reasons.push("not public");
  return reasons;
};

export const isHomeFreshFindsOptedIn = (stand = {}) =>
  stand.showInLocalFreshFinds === true ||
  (!hasRecordField(stand, "showInLocalFreshFinds") &&
    stand.isPublic !== false &&
    stand.visibility !== "private");

export const isHomePickupSpotDiscoverable = (stand = {}) =>
  isPublicFarmStand(stand) &&
  isPickupSpot(stand) &&
  isHomeFreshFindsOptedIn(stand);

export const prepareHomeFarmStandDiscoveryItems = (
  farmStands = [],
  viewerLocationData = {}
) => {
  const viewerLocation = normalizeLocationRecord(viewerLocationData || {});

  return farmStands
    .filter(isHomeFarmStandDiscoverable)
    .map((stand) => {
      const standLocation = normalizeLocationRecord({
        ...stand,
        lat: stand.lat ?? stand.publicLat,
        lng: stand.lng ?? stand.publicLng,
      });
      const distance = getSafeDistanceMiles(viewerLocation, standLocation);
      return {
        ...stand,
        freshStatus: getHomeStandStatus(stand),
        distance,
      };
    })
    .sort((a, b) => {
      if (a.distance != null && b.distance != null) return a.distance - b.distance;
      if (a.distance != null) return -1;
      if (b.distance != null) return 1;

      const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime() || 0;
      const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime() || 0;
      if (bTime !== aTime) return bTime - aTime;

      return getHomeStandName(a).localeCompare(getHomeStandName(b));
    });
};
